import * as Sentry from '@sentry/react';
import { debug as logger } from 'application/Shared/Utils';

/**
 * An array of environment names where error reporting should be enabled.
 * @type {string[]}
 */
const REPORTING_ENVIRONMENTS = ['production', 'stage', 'demo'];

/**
 * Initializes the Sentry error reporting service.
 *
 * @param {string} - The Sentry url used to for reporting.
 * @param {string} [environment='development'] - The environment to run Sentry in (e.g., 'development', 'production').
 * @param {string} [release='development_release'] - The release version or build number.
 *
 */
export function initSentry(
    dsn,
    environment = 'development',
    release = 'development_release'
) {
    if (REPORTING_ENVIRONMENTS.includes(environment)) {
        Sentry.init({
            dsn,
            environment,
            // Add here errors we want to ignore
            ignoreErrors: [],
            integrations: [
                // TODO: Enable once we add Sentry to the API.
                // Sentry.browserTracingIntegration(),
                Sentry.replayIntegration()
            ],
            release,
            // Session Replay
            replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                // TODO: Add API for trace propagation once we add Sentry to the API.
                // /^https:\/\/yourserver\.io\/api/
            ],
            // Tracing
            tracesSampleRate: 1.0 //  Capture 100% of the transactions
        });
    } else {
        logger.warn(
            'You are running in development mode Sentry reporting is disabled, to enable it change the REACT_APP_ENV variable.'
        );
    }
}

/**
 * Configures the Sentry scope with user, tenant, and other relevant data. Configuration won't happen if
 * session is missing or environment is not enabled.
 *
 * @param {Object} session - An object containing session data.
 * @param {string} session.id - The user's ID.
 * @param {Object} session.user - An object containing user data.
 * @param {string} session.user.admin - Indicates if the user is an admin.
 * @param {string} session.user.email - The user's email address.
 * @param {boolean} session.user.superAdmin - Indicates if the user is a super admin.
 * @param {string} session.user.theme - The user's theme preference.
 * @param {string} session.user.username - The user's username.
 * @param {string} session.sessionSettings.hubType - The type of hub.
 * @param {Object} session.tenant - An object containing tenant data.
 * @param {string} session.tenant.platformTenantId - The platform tenant ID.
 * @param {string} session.tenant.id - The tenant ID.
 * @param {string} session.tenant.identityTenantDomain - The identity tenant domain.
 * @param {string} session.tenant.region - The tenant's region.
 * @param {string} [environment='development'] - The environment (e.g., 'development', 'production').
 */
export function configureSentryScope(session, environment = 'development') {
    if (session !== undefined && REPORTING_ENVIRONMENTS.includes(environment)) {
        const { tenant, user, sessionSettings } = session;

        const { admin, email, id, superAdmin, theme, username } = user;
        const { hubType } = sessionSettings;

        // Add user data to error reports
        // Source: https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
        Sentry.setUser({ email, id, username });

        // Tenant info. We want to gather id, domain type and region.
        // Source: https://docs.sentry.io/platforms/javascript/enriching-events/scopes/
        Sentry.setTag('hubType', hubType);
        Sentry.setTag('platformTenantId', tenant.platformTenantId);
        Sentry.setTag('tenantId', tenant.id);
        Sentry.setTag('domainPrefix', tenant.identityTenantDomain);
        Sentry.setTag('region', tenant.region);

        // Add extra data to error reports (will not be indexed)
        Sentry.setExtra('theme', theme);
        Sentry.setExtra('admin', admin);
        Sentry.setExtra('superAdmin', superAdmin);
    }
}
